import React, { memo } from 'react';
import { TaskAPI } from '@business/api/task';
import { openTaskOverlay } from '@modules/tasks/TaskDetailModal';
import { Card, CardBody } from 'reactstrap';
import { TaskTextSearchResponse } from '@business/entities/task';
import { TaskService } from '@business/services';
import { toMoment } from '@core/utils/time';

interface TaskSearchResultsProps {
  results: TaskTextSearchResponse;
  isLoading: boolean;
  onClose: () => void;
}

export const TaskSearchResults = memo<TaskSearchResultsProps>(({ results, isLoading, onClose }) => {
  const handleTaskClick = (taskId: number, parentId?: number) => {
    // If it's a subtask, open the parent task with focus on the subtask
    const taskToOpen = taskId || parentId;
    openTaskOverlay({
      taskId: taskToOpen.toString(),
      onClose: () => console.log('closed'),
      // You might need to adjust this based on your task overlay implementation
    });
  };

  const handleCommentClick = (taskId: number, commentId: number) => {
    // Open the task and potentially scroll to the comment
    openTaskOverlay({
      taskId: taskId.toString(),
      onClose: () => console.log('closed'),
      // You might need to add comment focus functionality
    });
  };

  const formatHighlightedText = (text: string) => {
    // The text comes pre-highlighted from the backend with <mark> tags
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Card className="mt-3">
        <CardBody>
          <div className="text-center">
            <i className="fa fa-spinner fa-spin"></i> Searching...
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!results || results.totalResults === 0) {
    return (
      <Card className="mt-3">
        <CardBody>
          <div className="text-center text-muted">No results found for "{results?.query}"</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mt-3">
      <CardBody>
        <div className="search-results">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              Search Results for "{results.query}" ({results.totalResults} results)
            </h6>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              Clear Search
            </button>
          </div>

          {/* Parent Tasks */}
          {results.results.parentTasks.length > 0 && (
            <div className="search-section mb-4">
              <h6 className="text-primary font-weight-bold border-bottom pb-2">
                <i className="fas fa-tasks mr-2"></i>
                Parent Task Matches ({results.results.parentTasks.length})
              </h6>
              {results.results.parentTasks.map(task => {
                const isOverdue = !task.isCompleted && TaskService.isTaskOverdue(task.dueDate);
                return (
                  <div
                    key={`task-${task.id}`}
                    className="search-result-item p-3 border rounded mb-2 cursor-pointer hover-bg-light"
                    onClick={() => handleTaskClick(task.id)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className={`font-weight-bold mb-1 ${isOverdue ? 'text-danger font-weight-bold' : ''}`}>
                          {formatHighlightedText(task.title)}
                        </div>
                        {task.contentPreview && (
                          <div className={`small mb-2 ${isOverdue ? 'text-danger font-weight-bold' : 'text-muted'}`}>
                            {formatHighlightedText(task.contentPreview)}
                          </div>
                        )}
                        <div className="d-flex align-items-center">
                          {task.dueDate && (
                            <small className="text-muted mr-3">
                              <i className="fas fa-calendar mr-1"></i>
                              Due: {toMoment(task.dueDate).format('MM/DD/YYYY [at] h:mm A')}
                            </small>
                          )}
                          <small className={task.isCompleted ? 'text-success' : 'text-warning'}>
                            <i className={`fas ${task.isCompleted ? 'fa-check-circle' : 'fa-clock'} mr-1`}></i>
                            {task.isCompleted ? 'Completed' : 'Pending'}
                          </small>
                        </div>
                      </div>
                      <i className="fas fa-external-link-alt text-muted cursor-pointer"></i>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Subtasks */}
          {results.results.subtasks.length > 0 && (
            <div className="search-section mb-4">
              <h6 className="text-primary font-weight-bold border-bottom pb-2">
                <i className="fas fa-list-ul mr-2"></i>
                Subtask Matches ({results.results.subtasks.length})
              </h6>
              {results.results.subtasks.map(task => {
                const isOverdue = !task.isCompleted && TaskService.isTaskOverdue(task.dueDate);
                return (
                  <div
                    key={`subtask-${task.id}`}
                    className="search-result-item p-3 border rounded mb-2 cursor-pointer hover-bg-light"
                    onClick={() => handleTaskClick(task.id, task.parentId)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className={`font-weight-bold mb-1 ${isOverdue ? 'text-danger font-weight-bold' : ''}`}>
                          {task.parentTitle && (
                            <>
                              {task.parentTitle}
                              <i className="bx bx-right-arrow-circle mr-1 ml-1"></i>
                            </>
                          )}
                          {formatHighlightedText(task.title)}
                        </div>
                        {task.contentPreview && (
                          <div className={`small mb-2 ${isOverdue ? 'text-danger font-weight-bold' : 'text-muted'}`}>
                            {formatHighlightedText(task.contentPreview)}
                          </div>
                        )}
                        <div className="d-flex align-items-center">
                          {task.dueDate && (
                            <small className="text-muted mr-3">
                              <i className="fas fa-calendar mr-1"></i>
                              Due: {formatDate(task.dueDate)}
                            </small>
                          )}
                          <small className={task.isCompleted ? 'text-success' : 'text-warning'}>
                            <i className={`fas ${task.isCompleted ? 'fa-check-circle' : 'fa-clock'} mr-1`}></i>
                            {task.isCompleted ? 'Completed' : 'Pending'}
                          </small>
                        </div>
                      </div>
                      <i className="fas fa-external-link-alt text-muted cursor-pointer"></i>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Comments */}
          {results.results.comments.length > 0 && (
            <div className="search-section mb-4">
              <h6 className="text-primary font-weight-bold border-bottom pb-2">
                <i className="fas fa-comments mr-2"></i>
                Comment Matches ({results.results.comments.length})
              </h6>
              {results.results.comments.map(comment => (
                <div
                  key={`comment-${comment.commentId}`}
                  className="search-result-item p-3 border rounded mb-2 cursor-pointer hover-bg-light"
                  onClick={() => handleCommentClick(comment.taskId!, comment.commentId!)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="font-weight-bold mb-1">{comment.taskTitle}</div>
                      <div className="font-weight-bold small mb-1">Comment by {comment.authorName}</div>
                      <div className="text-muted small mb-2">{formatHighlightedText(comment.commentPreview)}</div>
                      {comment.createdAt && (
                        <small className="text-muted">
                          <i className="fas fa-clock mr-1"></i>
                          {toMoment(comment.createdAt).format('MM/DD/YYYY [at] h:mm A')}
                        </small>
                      )}
                    </div>
                    <i className="fas fa-external-link-alt text-muted cursor-pointer"></i>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
});
